
import { useEffect, useCallback, useSyncExternalStore } from 'react';
import { useSession } from '@/lib/auth-client';
import { walletAPI } from '@/lib/api/wallet';

const REFRESH_INTERVAL_MS = 60_000; // 60 seconds

// ---------------------------------------------------------------------------
// Shared singleton: one polling interval regardless of how many hook instances
// ---------------------------------------------------------------------------

interface WalletState {
  balance: string | null;
  lowBalance: boolean;
  isLoading: boolean;
  error: string | null;
}

let sharedState: WalletState = { balance: null, lowBalance: false, isLoading: false, error: null };
let subscriberCount = 0;
let pollingInterval: ReturnType<typeof setInterval> | null = null;
let fetchInFlight = false;
const listeners = new Set<() => void>();

function emitChange() {
  for (const listener of listeners) {
    listener();
  }
}

function setSharedState(partial: Partial<WalletState>) {
  sharedState = { ...sharedState, ...partial };
  emitChange();
}

let backoffUntil = 0; // Timestamp — skip fetches until this time (after 429)

async function fetchBalanceShared() {
  if (fetchInFlight) return;
  // Respect backoff after rate limit
  if (Date.now() < backoffUntil) return;

  fetchInFlight = true;
  setSharedState({ isLoading: true, error: null });

  try {
    const result = await walletAPI.getBalance(true);
    setSharedState({ balance: result.balance, lowBalance: result.lowBalance ?? false, isLoading: false });
  } catch (err) {
    // Debug: log the actual error so we can see what's failing
    console.error('[WalletBalance] fetch failed:', err);
    // Back off on rate limit (429) — wait 2 minutes before retrying
    const errMsg = err instanceof Error ? err.message : '';
    const errStatus = (err as { status?: number }).status;
    if (errStatus === 429 || errMsg.toLowerCase().includes('too many')) {
      backoffUntil = Date.now() + 120_000;
    }
    setSharedState({
      error: err instanceof Error ? err.message : 'Failed to load balance',
      isLoading: false,
    });
  } finally {
    fetchInFlight = false;
  }
}

function handleBalanceChangedEvent() {
  void fetchBalanceShared();
}

function startPolling() {
  if (pollingInterval) return;
  backoffUntil = 0; // Reset backoff on new polling session
  // Immediate first fetch
  void fetchBalanceShared();
  pollingInterval = setInterval(() => {
    void fetchBalanceShared();
  }, REFRESH_INTERVAL_MS);
  // Listen for immediate balance refresh events (top-up, job completion)
  window.addEventListener('genie:balance-changed', handleBalanceChangedEvent);
}

function stopPolling() {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
  window.removeEventListener('genie:balance-changed', handleBalanceChangedEvent);
}

function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
}

function getSnapshot(): WalletState {
  return sharedState;
}

function getServerSnapshot(): WalletState {
  return { balance: null, lowBalance: false, isLoading: false, error: null };
}

// ---------------------------------------------------------------------------
// Public hook — same API as before, but all instances share one poller
// ---------------------------------------------------------------------------

interface UseWalletBalanceReturn {
  balance: string | null;
  isLowBalance: boolean;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useWalletBalance(): UseWalletBalanceReturn {
  const session = useSession();
  const isAuthenticated = !!session.data?.user;

  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Reference-counted polling: first subscriber starts, last stops
  useEffect(() => {
    if (!isAuthenticated) return;

    subscriberCount++;
    if (subscriberCount === 1) {
      startPolling();
    }

    return () => {
      subscriberCount--;
      if (subscriberCount === 0) {
        stopPolling();
      }
    };
  }, [isAuthenticated]);

  // Stable refetch callback
  const refetch = useCallback(() => {
    if (isAuthenticated) {
      void fetchBalanceShared();
    }
  }, [isAuthenticated]);

  // When not authenticated, return empty state
  if (!isAuthenticated) {
    return { balance: null, isLowBalance: false, isLoading: false, error: null, refetch };
  }

  return {
    balance: state.balance,
    isLowBalance: state.lowBalance,
    isLoading: state.isLoading,
    error: state.error,
    refetch,
  };
}
